import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';

export interface City {
    id: number;
    name: string;
    name_ar: string;
    code: string;
}

export interface District {
    id: number;
    name: string;
    name_ar: string;
    code: string;
    city_id: number;
}

export interface Neighborhood {
    id: number;
    name: string;
    name_ar: string;
    code: string;
    city_id: number;
    district_id: number;
}

export interface AddressSearchResult {
    id: number;
    name: string;
    name_ar: string;
    code: string;
    city_id: number;
    district_id?: number;
    type: 'district' | 'neighborhood';
    displayName: string;
    district_name?: string;
}

@Injectable({
    providedIn: 'root'
})
export class AddressService {
    private baseUrl = environment.api.serverUrl;

    constructor(private http: HttpClient) {}

    /**
     * Get all cities (gouvernorats)
     */
    getCities(): Promise<City[]> {
        const citiesUrl = environment.api.addresses || `${this.baseUrl}/addresses`;
        return this.http.get<{success: boolean, data: City[]}>(`${citiesUrl}/cities`)
            .toPromise()
            .then(response => response.data);
    }

    /**
     * Get districts by city ID
     */
    getDistrictsByCity(cityId: number): Promise<District[]> {
        const addressesUrl = environment.api.addresses || `${this.baseUrl}/addresses`;
        return this.http.get<{success: boolean, data: District[]}>(`${addressesUrl}/districts/${cityId}`)
            .toPromise()
            .then(response => response.data);
    }

    /**
     * Get neighborhoods by city ID and optional district ID
     */
    getNeighborhoodsByCity(cityId: number, districtId?: number): Promise<Neighborhood[]> {
        const addressesUrl = environment.api.addresses || `${this.baseUrl}/addresses`;
        let url = `${addressesUrl}/neighborhoods/${cityId}`;
        if (districtId) {
            url += `?districtId=${districtId}`;
        }
        return this.http.get<{success: boolean, data: Neighborhood[]}>(url)
            .toPromise()
            .then(response => response.data);
    }

    /**
     * Search addresses (districts and neighborhoods) with autocomplete
     */
    searchAddresses(cityId: number, term: string): Promise<AddressSearchResult[]> {
        const addressesUrl = environment.api.addresses || `${this.baseUrl}/addresses`;
        return this.http.get<{success: boolean, data: AddressSearchResult[]}>(`${addressesUrl}/search?cityId=${cityId}&term=${encodeURIComponent(term)}`)
            .toPromise()
            .then(response => response.data);
    }
}